// ── WHAT ONE PREVENTED ACRE PAYS ─────────────────────────────────────────────
//
// FCIC-25370 ¶75(1)(a):
//
//     PP indemnity = PP factor x (approved yield x coverage level x PROJECTED price)
//                  x prevent-plant acres x share
//
// ⚠ THIS EXISTED TWICE AND THE TWO COPIES DISAGREED. `estimatePreventPlant` computed
// `factor x (aph x level x price)` with no rounding; `modelledPpExpectation` computed
// `round(aph x level x factor, 0.1) x price`. Same inputs, different dollars — and the
// second is the validated one, so the first was quietly wrong on every line it priced.
//
// ⚠ THE ROUNDING IS SOURCED, NOT FITTED. RMA states a per-acre GUARANTEE QUANTITY
// (P11 #120, `acre_guarantee_qty`) with every adjustment already in it, and the dollars
// follow from that — so the arithmetic is ROUND-THEN-MULTIPLY. Verified against the
// AIP's own stated field rather than back-solved from liability: of the 123 CY2026
// prevent-plant units carrying `acre_guarantee_qty`, **118 are exactly one decimal
// place**. Computing it the other way left residuals of $18 to $45 on ~$120,000 across
// seven real units — small enough to read as noise, and it would have made every row
// differ by a little forever. A check whose rows never tie exactly is a check nobody
// trusts.
//
// ⚠ AND THE STORED `acre_guarantee_qty` IS DELIBERATELY NOT USED. It is the AIP's own
// figure. We recompute it; where ours and theirs disagree THAT is the finding, and
// reading theirs is the one certain way to never see it.

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));

// Money runs in integer cents everywhere it is summed — a frozen estimate is compared
// to the penny against what the carrier paid.
export const cents = (v) => Math.round((num(v) || 0) * 100);
export const dollars = (c) => c / 100;

// The per-acre guarantee QUANTITY, in bushels (or tons for silage), to one decimal.
// Returns null rather than 0 when an input is missing: a zero guarantee is a real and
// very different statement from "we could not compute one".
export function acreGuaranteeQty(aph, coverageLevel, ppFactor) {
  const y = num(aph), cl = num(coverageLevel), f = num(ppFactor);
  if (y == null || y <= 0 || cl == null || cl <= 0 || f == null || f <= 0) return null;
  return Math.round(y * cl * f * 10) / 10;
}

// Dollars per prevented acre, BEFORE share. Share is applied last (¶75(1)) and on
// rolled acres it is the QUALIFYING unit's, never the lender's (¶84B(1)(b)) — which is
// why it is not an argument here.
//
// ⚠ THE PRICE ELECTION PERCENTAGE IS A REQUIRED ARGUMENT AND HAS NO DEFAULT. A grower
// may insure at less than the full projected price — CAT is 50% coverage at 55% price —
// and `crop_coverage.price_election_pct` carries it. Omitted, a CAT acre prices at
// 1/0.55 = **1.82x what the carrier will pay**.
//
// That is not hypothetical: it is how this rule's first live comparison failed. Measured
// on the CY2026 book, BAINBRIDGE FARM LLC's ALLEGANY corn (plan YP, level 0.50, election
// 0.55) came out **+81.2% against the AIP's own stated liability on every unit**, and
// across the book **31 of 177 prevent-plant units carry a reduced election — 865.7 acres,
// $241,818 of liability**.
//
// It has no default BECAUSE a default is what hides it. This repo's record on an omitted
// optional argument is unambiguous — `matchSupplementals()` without `baseRows` double-
// rendered $3.3M, `guaranteeBushels()` without `priceElectionPct` read 45% short on three
// farms. THE ARITY IS THE CONTRACT. A caller that does not resolve the election passes
// `1` explicitly, at the call site, where a reader can see the assumption.
export function ppDollarsPerAcre(aph, coverageLevel, ppFactor, projectedPrice, priceElectionPct) {
  const q = acreGuaranteeQty(aph, coverageLevel, ppFactor);
  const px = num(projectedPrice);
  const pe = num(priceElectionPct);
  // ⚠ A NULL ELECTION IS NOT 100%. 17 coverages sit at a CAT coverage level with no
  // election recorded, so "not stated" genuinely may not be full — it is unpriceable.
  if (q == null || px == null || px <= 0 || pe == null || pe <= 0 || pe > 1) return null;
  return q * px * pe;
}
